import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { 
  FileText, 
  Trash2,
  Loader2,
  Upload,
  ArrowLeft,
  Table2,
  Clock,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExtractionsPage = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [extractions, setExtractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [extractionToDelete, setExtractionToDelete] = useState(null);

  const fetchExtractions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/extractions`);
      setExtractions(response.data);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load extractions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExtractions();
  }, []);

  const handleDelete = async () => {
    if (!extractionToDelete) return;
    
    try {
      await axios.delete(`${API}/extraction/${extractionToDelete.id}`);
      setExtractions(prev => prev.filter(e => e.id !== extractionToDelete.id));
      toast.success("Extraction deleted");
    } catch (err) {
      toast.error("Failed to delete extraction");
    } finally {
      setDeleteDialogOpen(false);
      setExtractionToDelete(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-1 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h1 className="font-heading text-2xl font-bold text-gray-900" data-testid="extractions-title">
                    Recent Uploads
                  </h1>
                  <p className="text-gray-500 mt-1">
                    PDFs you've uploaded - click to view and create batches
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/")}
                  data-testid="back-home-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                {isAdmin && (
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => navigate("/")}
                    data-testid="upload-new-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload New
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-20 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : extractions.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-20 text-center">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Uploads Yet</h3>
              <p className="text-gray-500 mb-6">
                Upload a PDF to extract structural framing schedules
              </p>
              {isAdmin && (
                <Button onClick={() => navigate("/")} className="bg-blue-600 hover:bg-blue-700">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload PDF
                </Button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-fade-in delay-100">
              <div className="divide-y divide-gray-100">
                {extractions.map((extraction, idx) => (
                  <div 
                    key={extraction.id}
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer flex items-center justify-between gap-4"
                    onClick={() => navigate(`/results/${extraction.id}`)}
                    data-testid={`extraction-row-${idx}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                          {extraction.filename}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700">
                            <Table2 className="w-3 h-3 mr-1" />
                            {extraction.table_count} tables
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {extraction.page_count} pages
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatFileSize(extraction.file_size)}
                          </span>
                          {extraction.ocr_used && (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">
                              OCR
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Uploaded
                        </p>
                        <p className="text-sm text-gray-600">{formatDate(extraction.created_at)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/results/${extraction.id}`);
                        }}
                        data-testid={`view-extraction-${idx}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExtractionToDelete(extraction);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-extraction-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Extraction?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{extractionToDelete?.filename}" and allow you to re-upload this PDF. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExtractionsPage;
